import {
  AnalyticsApiClient,
  AnalyticsExchangeRate,
  AnalyticsMetaData,
  DatalligatorResult,
} from "@ternary/api-lib/analytics/api/AnalyticsApiClient";
import DataQuery from "@ternary/api-lib/analytics/api/DataQuery";
import DataQueryExperimental from "@ternary/api-lib/analytics/api/DataQueryExperimental";
import { RawData } from "@ternary/api-lib/analytics/types";
import { CurrencyCode } from "@ternary/api-lib/constants/enums";
import { EventReporter } from "@ternary/api-lib/telemetry";
import UError from "unilib-error";
import HTTPRequestSender from "../HTTPRequestSender";

const FAILED_CALCULATING_BYTES_MESSAGE = "Failed to calculate size in bytes";
export const MAX_ROWS = 50000;
const QUERY_DURATION_MAX_TIME = 60000;

type ForecastingResult<TData> = {
  forecast: TData[];
} & DatalligatorResult<TData>;

interface Config {
  analyticsSender: HTTPRequestSender;
  devMode: boolean;
  eventReporter: EventReporter;
}

export default class AnalyticsApiClientImpl implements AnalyticsApiClient {
  private readonly _className = "AnalyticsApiClient";

  private readonly _analyticsSender: HTTPRequestSender;
  private readonly _devMode: boolean;
  private readonly _eventReporter: EventReporter;

  constructor(config: Config) {
    this._analyticsSender = config.analyticsSender;
    this._devMode = config.devMode;
    this._eventReporter = config.eventReporter;
  }

  public async getMetadata(
    tenantID: string
  ): Promise<AnalyticsMetaData["schemas"]> {
    try {
      const result = (await this._analyticsSender.send(
        `/meta/focus?tenant_id=${tenantID}`
      )) as AnalyticsMetaData;

      return result.schemas;
    } catch (error) {
      this._eventReporter.reportError(error);

      throw new UError(`${this._className}.getMetadata/ERROR_UNEXPECTED`, {
        cause: error,
      });
    }
  }

  public async getExchangeRates(
    tenantID: string,
    baseCurrencyCode: CurrencyCode = CurrencyCode.USD,
    quoteCurrencyCode?: CurrencyCode
  ): Promise<AnalyticsExchangeRate> {
    try {
      const searchParams = new URLSearchParams({
        tenant_id: tenantID,
        base_currency: baseCurrencyCode,
      });

      if (quoteCurrencyCode) {
        searchParams.set("quote_currency", quoteCurrencyCode);
      }

      const result = (await this._analyticsSender.send(
        `/meta/exchange_rates/latest?${searchParams.toString()}`
      )) as AnalyticsExchangeRate;

      return result;
    } catch (error) {
      this._eventReporter.reportError(error);

      throw new UError(`${this._className}.getMetadata/ERROR_UNEXPECTED`, {
        cause: error,
      });
    }
  }

  public async loadData<TData extends RawData = RawData>(
    tenantID: string,
    query: DataQuery
  ): Promise<DatalligatorResult<TData>> {
    try {
      const startTime = new Date().getTime();

      const result = (await this._analyticsSender.send(
        `/query/load?tenant_id=${tenantID}`,
        {
          method: "POST",
          body: query,
        }
      )) as DatalligatorResult<TData>;

      if (!this._devMode) {
        this._captureQueryData(query, result.response, startTime);
      }

      return result;
    } catch (error) {
      this._eventReporter.reportError(error);

      throw new UError(`${this._className}.loadData/ERROR_UNEXPECTED`, {
        cause: error,
      });
    }
  }

  public async loadDataExperimental<TData extends RawData = RawData>(
    tenantID: string,
    query: DataQueryExperimental
  ): Promise<ForecastingResult<TData>> {
    try {
      const startTime = new Date().getTime();

      const result = (await this._analyticsSender.send(
        `/x/query/load?tenant_id=${tenantID}`,
        {
          method: "POST",
          body: query,
        }
      )) as ForecastingResult<TData>;

      if (!this._devMode) {
        this._captureQueryData(query, result.response, startTime);
      }

      return result;
    } catch (error) {
      this._eventReporter.reportError(error);

      throw new UError(`${this._className}.loadData/ERROR_UNEXPECTED`, {
        cause: error,
      });
    }
  }

  private _captureQueryData<TData = RawData>(
    query: DataQuery,
    result: TData[],
    startTime: number
  ): void {
    const queryDuration = (new Date().getTime() - startTime) / 1000;
    const resultLength = result.length;

    let sizeInBytes: number;

    try {
      sizeInBytes = new TextEncoder().encode(JSON.stringify(result)).length;
    } catch {
      this._eventReporter.reportMessage(FAILED_CALCULATING_BYTES_MESSAGE);
      sizeInBytes = -1;
    }

    this._eventReporter.reportEvent("data_query", {
      dimensions: query.dimensions,
      duration_seconds: queryDuration,
      measures: query.measures,
      ...(typeof window !== "undefined"
        ? { page_path: window.location.pathname }
        : {}),
      response_length: resultLength,
      size_in_bytes: sizeInBytes,
      type: "datalligator",
    });

    if (queryDuration > QUERY_DURATION_MAX_TIME) {
      const message = `WARNING: Query duration exceeded ${QUERY_DURATION_MAX_TIME}ms.`;

      this._eventReporter.reportMessage(message, { context: { query } });
    }

    if (resultLength === MAX_ROWS) {
      this._eventReporter.reportEvent("QUERY_SIZE_LIMIT_EXCEEDED", {
        query,
      });
    }
  }
}
